import { DamEcoModeVersion } from '~/providers/types/damEcoMode';

export interface DamEcoModeViewInput {
  /** Se o firmware do DAM suporta o Modo Eco simplificado (informado pelo backend). */
  supported: boolean;
  activeMode: DamEcoModeVersion;
  isAdminDiel: boolean;
}

export interface DamEcoModeView {
  /** Telas exibidas, na ordem das abas. */
  screens: DamEcoModeVersion[];
  /** Telas exibidas só para leitura. */
  readOnlyScreens: DamEcoModeVersion[];
  /** Se o usuário vê qual modo está ativo e pode alternar entre eles. */
  canSwitch: boolean;
}

const ALL_SCREENS: DamEcoModeVersion[] = ['LEGACY', 'SIMPLIFIED'];

/**
 * Regra de exibição da aba Automação do DAM com o Modo Eco:
 * - DAM sem suporte: só a tela atual, editável como sempre foi;
 * - admin Diel em DAM suportado: as duas telas, o modo ativo indicado e a alternância; a tela do modo
 *   inativo fica só para leitura, então editar exige alternar antes;
 * - demais usuários em DAM suportado: só a tela do modo ativo, sem alternância.
 */
export function getDamEcoModeView({ supported, activeMode, isAdminDiel }: DamEcoModeViewInput): DamEcoModeView {
  if (!supported) {
    return { screens: ['LEGACY'], readOnlyScreens: [], canSwitch: false };
  }
  if (isAdminDiel) {
    return {
      screens: ALL_SCREENS,
      readOnlyScreens: ALL_SCREENS.filter((screen) => screen !== activeMode),
      canSwitch: true,
    };
  }
  return { screens: [activeMode], readOnlyScreens: [], canSwitch: false };
}

/** Tela a exibir: a escolhida pelo usuário, se ela estiver disponível; senão, a do modo ativo. */
export function resolveDamEcoModeScreen(view: DamEcoModeView, activeMode: DamEcoModeVersion, selected: DamEcoModeVersion | null): DamEcoModeVersion {
  if (selected && view.screens.includes(selected)) return selected;
  return view.screens.includes(activeMode) ? activeMode : view.screens[0];
}

/** Onde há alterações não salvas que bloqueiam a alternância de modo. */
export interface DamEcoModePendingChanges {
  simplified: boolean;
  legacy: boolean;
}

export type DamEcoModeSwitchBlockedKey =
  | 'ecoSimplificadoAlternarBloqueado'
  | 'ecoSimplificadoAlternarBloqueadoTelaAtual'
  | 'ecoSimplificadoAlternarBloqueadoAmbasTelas';

/**
 * Orientação para liberar a alternância. A tela do modo atual não tem "Descartar": lá, as alterações
 * se descartam recarregando a página.
 */
export function getSwitchBlockedMessageKey(pending: DamEcoModePendingChanges): DamEcoModeSwitchBlockedKey | null {
  if (pending.simplified && pending.legacy) return 'ecoSimplificadoAlternarBloqueadoAmbasTelas';
  if (pending.legacy) return 'ecoSimplificadoAlternarBloqueadoTelaAtual';
  if (pending.simplified) return 'ecoSimplificadoAlternarBloqueado';
  return null;
}
